import {useAddPatientTherapySequenceObservation} from '@/modules/patients-therapies/api/use-add-patient-therapy-sequence-observation'
import {EmptyState} from '@cypro/ui/components/ui/empty-state'
import {usePatientTherapySequenceContext} from '@/shared/contexts/patient-therapy-sequence-context'
import {useAppForm} from '@cypro/ui/integrations/form'
import {toaster} from '@cypro/ui/toaster'
import {getMonthDayTimeFormatter} from '@cypro/ui/utils'
import {Avatar, Box, Card, Stack, Tabs, Text, VStack} from '@chakra-ui/react'
import {AddPatientTherapySequenceObservation} from '@cypro/engine/mutators'
import {revalidateLogic} from '@tanstack/react-form'
import {useTranslate} from '@tolgee/react'
import {SubmitEvent} from 'react'
import * as z from 'zod'

const ObservationFormSchema = z.object({
  note: z.string().trim().nonempty(),
})

export function ObservationsTab() {
  const {t} = useTranslate('common')
  const {t: tActivity} = useTranslate('activity')
  const {sequence} = usePatientTherapySequenceContext()

  const addObservation = useAddPatientTherapySequenceObservation()

  const form = useAppForm({
    validators: {onDynamic: ObservationFormSchema},
    validationLogic: revalidateLogic(),
    defaultValues: {note: ''},
    onSubmit({value}) {
      return onAddObservation({sequenceId: sequence.id, note: value.note})
    },
  })

  async function onAddObservation(payload: AddPatientTherapySequenceObservation) {
    const res = await addObservation(payload)
    if (!res) return

    form.reset()
    toaster.success({title: t('success'), description: tActivity('observation_added_successfully')})
  }

  function onSubmit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault()
    form.handleSubmit()
  }

  return (
    <Tabs.Content value="observations" spaceY="6" flex="1" display="flex" flexDirection="column" minHeight="0">
      <Box
        asChild
        p="5"
        borderWidth="1px"
        borderStyle="solid"
        borderColor="border"
        bg="bg.subtle"
        borderRadius="lg"
        display="flex"
        flexDirection="column"
        alignItems="flex-end"
        gapY="2"
      >
        <form onSubmit={onSubmit}>
          <form.AppForm>
            <form.AppField name="note">
              {field => (
                <field.Textarea
                  label={t('note')}
                  hideLabel
                  placeholder={tActivity('observation_note_placeholder')}
                  rows={4}
                />
              )}
            </form.AppField>

            <form.SubmitButton variant="surface" width="max-content" size="sm">
              {t('add_note')}
            </form.SubmitButton>
          </form.AppForm>
        </form>
      </Box>

      {!sequence.observations?.length && <EmptyState title={tActivity('no_observations_yet')} p="0" />}

      {Boolean(sequence.observations?.length) && (
        <VStack gapY="0" divideY="1px" divideColor="border" overflowY="auto">
          {sequence.observations?.map(obs => (
            <Box key={obs.id} width="100%">
              <Card.Root border="none">
                <Card.Body p="4">
                  <Box display="flex" gap="3">
                    <Avatar.Root>
                      <Avatar.Fallback name="Nate Foss" />
                    </Avatar.Root>
                    <Stack gap="0">
                      <Text fontWeight="medium" textStyle="sm">
                        {obs.created?.name} -{' '}
                        <Text as="span" color="fg.muted">
                          {obs.created?.groups?.map(group => group.name).join(', ')}
                        </Text>
                      </Text>
                      <Text color="fg.muted" textStyle="xs" marginTop="0.5">
                        {obs.createdAt && getMonthDayTimeFormatter().format(new Date(obs.createdAt))}
                      </Text>

                      <Text marginTop="2.5" textStyle="sm" color="fg.muted" wordBreak="break-all" whiteSpace="pre-line">
                        {obs.note}
                      </Text>
                    </Stack>
                  </Box>
                </Card.Body>
              </Card.Root>
            </Box>
          ))}
        </VStack>
      )}
    </Tabs.Content>
  )
}
